/* =====================================================================================
   Purchase orders: commitment before spend, same routing as expenses, thresholds the
   organisation already set. Everything through ledger functions; reads via RLS.
   ===================================================================================== */

import { BACKEND } from './db';
import { explain } from './dberrors';

const live = () => BACKEND === 'supabase';

export const PO_CATEGORIES = [
  ['goods', 'Goods'], ['services', 'Services'], ['software', 'Software'],
  ['lab', 'Laboratory'], ['travel', 'Travel'], ['marketing', 'Marketing'],
  ['capital', 'Capital item'], ['other', 'Other'],
] as const;

export interface PO {
  id: string; reference: string; supplier_name: string; description: string;
  category: string; amount: number; currency: string; needed_by: string | null;
  justification: string | null; quote_ref: string | null; is_capital: boolean;
  status: string; requested_by: string | null; approver_id: string | null;
  approver_source: string | null; auto_approved: boolean;
  decision_note: string | null; created_at: string;
}

export async function fetchPOs(): Promise<PO[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('ledger').from('purchase_orders')
    .select('*').order('created_at', { ascending: false }).limit(200);
  if (error) throw new Error(explain(error.message, 'read the purchase orders'));
  return (data ?? []) as PO[];
}

export async function fetchCommitment(): Promise<{ currency: string; committed: number;
  awaiting_decision: number; overdue_delivery: number }[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data } = await supabase().schema('ledger').from('v_open_commitment').select('*');
  return (data ?? []) as never;
}

async function rpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('ledger').rpc(fn, args as never);
  if (error) throw new Error(explain(error.message, 'update the purchase order'));
  return data as T;
}

export const createPO = (p: { supplier: string; description: string; amount: number;
  currency?: string; category: string; neededBy?: string; justification?: string; quoteRef?: string }) =>
  rpc<string>('create_po', { p_supplier: p.supplier, p_description: p.description,
    p_amount: p.amount, p_currency: p.currency || null, p_category: p.category,
    p_needed_by: p.neededBy || null, p_justification: p.justification || null,
    p_quote_ref: p.quoteRef || null, p_supplier_account: null });

export const decidePO = (id: string, approve: boolean, note?: string) =>
  rpc<void>('decide_po', { p_id: id, p_approve: approve, p_note: note || null });

export const progressPO = (id: string, to: 'ordered' | 'received' | 'cancelled', note?: string) =>
  rpc<void>('progress_po', { p_id: id, p_to: to, p_note: note || null });

export async function myMemberId(): Promise<string | null> {
  if (!live()) return null;
  const { supabase } = await import('./supabase');
  const { data } = await supabase().schema('app').rpc('current_member_id');
  return (data as string) ?? null;
}
